<template>
  <div class="content" style="padding: 16px 0">

    <!-- KPI Dashboard -->
    <v-row dense>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined style="border-top: 3px solid #e2007a">
          <v-card-text>
            <div class="kpi-row">
              <v-icon large color="#e2007a">mdi-file-document-outline</v-icon>
              <div>
                <div class="kpi-value">{{ stats.bdcTotal || 0 }}</div>
                <div class="kpi-label">BDC V2 total</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined style="border-top: 3px solid #2196f3">
          <v-card-text>
            <div class="kpi-row">
              <v-icon large color="#2196f3">mdi-file-export</v-icon>
              <div>
                <div class="kpi-value">{{ bdcAExporter }}</div>
                <div class="kpi-label">À exporter</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined style="border-top: 3px solid #4caf50">
          <v-card-text>
            <div class="kpi-row">
              <v-icon large color="#4caf50">mdi-check-circle-outline</v-icon>
              <div>
                <div class="kpi-value">{{ stats.bdcSoumisAsp || 0 }}</div>
                <div class="kpi-label">Soumis ASP</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined style="border-top: 3px solid #ff9800">
          <v-card-text>
            <div class="kpi-row">
              <v-icon large color="#ff9800">mdi-currency-eur</v-icon>
              <div>
                <div class="kpi-value">{{ formatMontant(stats.totalPPHT) }}</div>
                <div class="kpi-label">Total PP HT</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Section BDC : Demande d'avance (40%) -->
    <v-card outlined class="mt-3">
      <v-card-title class="section-title">
        <v-icon left color="#e2007a" small>mdi-file-send-outline</v-icon>
        Bons de commande : demande d'avance (40%)
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-row>
          <!-- Export BDC -->
          <v-col cols="12" md="6">
            <div class="asp-block">
              <div class="asp-block-title">
                <v-icon small color="#e2007a" class="mr-1">mdi-upload</v-icon>
                Exporter vers l'ASP
              </div>
              <v-select
                v-model="nbExport"
                :items="exportOptions"
                label="Nombre de BDC à exporter"
                outlined
                dense
                class="mt-2"
              ></v-select>
              <v-btn
                color="#e2007a"
                dark
                :loading="loadingExport"
                @click="exportBdc"
              >
                Exporter les BDC
                <v-icon right>mdi-file-export</v-icon>
              </v-btn>

              <div v-if="lastExportDate" class="asp-last-action mt-3">
                Dernier export : {{ formatDate(lastExportDate) }}
              </div>

              <!-- Erreurs de pré-validation export -->
              <v-alert
                v-if="exportErrors && exportErrors.errorSummary"
                type="error"
                outlined
                dense
                class="mt-3"
              >
                <div style="font-weight: bold; margin-bottom: 4px">
                  Erreurs de pré-validation ({{ exportErrors.errorSummary.total }})
                </div>
                <template v-for="cat in errorCategories">
                  <div
                    v-if="exportErrors.errorSummary[cat] && exportErrors.errorSummary[cat].count > 0"
                    :key="cat"
                  >
                    {{ cat }} : {{ exportErrors.errorSummary[cat].count }} erreur(s)
                  </div>
                </template>
              </v-alert>
              <div v-if="exportErrors && exportErrors.errors" class="mt-2">
                <div
                  v-for="(e, idx) in exportErrors.errors"
                  :key="'err-' + idx"
                  class="asp-error-line"
                >
                  <strong>[{{ e.rule }}]</strong>
                  <span v-if="e.level === 'ROW'" style="color: #444">
                    BDC {{ formatNumeroEditeur(e.numeroEditeur) }} - {{ e.displayName }}
                    <span v-if="e.RPPS" style="color: #888">(RPPS {{ e.RPPS }})</span>
                    <span v-else-if="e.FINESSJuridique" style="color: #888">(FINESS {{ e.FINESSJuridique }})</span>
                  </span>
                  <span v-else style="color: #888"> (entete fichier)</span>
                  <div>{{ e.message }}</div>
                  <div v-if="e.correctiveAction" class="asp-corrective">
                    &rarr; {{ e.correctiveAction }}
                  </div>
                </div>
              </div>
            </div>
          </v-col>

          <!-- Import retour BDC -->
          <v-col cols="12" md="6">
            <div class="asp-block">
              <div class="asp-block-title">
                <v-icon small color="#4caf50" class="mr-1">mdi-download</v-icon>
                Importer le retour ASP
              </div>
              <v-file-input
                v-model="bdcImportFile"
                label="Fichier JSON retour ASP"
                accept="application/json"
                outlined
                dense
                prepend-icon="mdi-file-code-outline"
                class="mt-2"
              ></v-file-input>
              <v-btn
                color="#e2007a"
                dark
                :loading="loadingImport"
                :disabled="!bdcImportFile"
                @click="importReturnBdc"
              >
                Importer le retour
                <v-icon right>mdi-file-import</v-icon>
              </v-btn>

              <div v-if="lastImportDate" class="asp-last-action mt-3">
                Dernier import : {{ formatDate(lastImportDate) }}
              </div>

              <!-- Résultat import -->
              <div v-if="importResult" class="mt-3">
                <v-alert
                  :type="importResult.summary.rejected > 0 ? 'warning' : 'success'"
                  outlined
                  dense
                >
                  {{ importResult.summary.accepted }} accepté(s),
                  {{ importResult.summary.rejected }} rejeté(s),
                  {{ importResult.summary.notFound }} non trouvé(s)
                  sur {{ importResult.summary.total }} ligne(s)
                </v-alert>

                <div
                  v-for="(d, idx) in importResult.details"
                  :key="'imp-' + idx"
                  class="asp-import-line"
                >
                  <v-icon x-small :color="statusColor(d.status)" class="mr-1">
                    {{ statusIcon(d.status) }}
                  </v-icon>
                  <span v-if="d.status === 'ACCEPTED'" style="color: #4caf50">
                    {{ d.numeroEditeur }} — accepté (ASP: {{ d.numeroASP }})
                  </span>
                  <span v-else-if="d.status === 'REJECTED'" style="color: #f44336">
                    {{ d.numeroEditeur }} — rejeté
                    <span v-if="d.category"> [{{ d.category }}]</span>
                    : {{ d.error }}
                  </span>
                  <span v-else style="color: #ff9800">
                    {{ d.numeroEditeur }} — non trouvé en base
                  </span>
                  <div v-if="d.correctiveAction" class="asp-corrective">
                    &rarr; {{ d.correctiveAction }}
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-progress-linear
        v-if="loadingExport || loadingImport"
        indeterminate
        color="#e2007a"
      ></v-progress-linear>
    </v-card>

    <!-- Section Factures : Demande de solde (60%) -->
    <v-card outlined class="mt-3 mb-4">
      <v-card-title class="section-title">
        <v-icon left color="#ff9800" small>mdi-receipt-text-outline</v-icon>
        Factures : demande de solde (60%)
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-row>
          <!-- Export factures -->
          <v-col cols="12" md="6">
            <div class="asp-block">
              <div class="asp-block-title">
                <v-icon small color="#ff9800" class="mr-1">mdi-upload</v-icon>
                Exporter les factures
              </div>
              <v-select
                v-model="nbExportFact"
                :items="exportOptions"
                label="Nombre de factures à exporter"
                outlined
                dense
                class="mt-2"
              ></v-select>
              <v-btn
                color="#ff9800"
                dark
                :loading="loadingExportFact"
                @click="exportFactures"
              >
                Exporter les factures
                <v-icon right>mdi-file-export</v-icon>
              </v-btn>
            </div>
          </v-col>

          <!-- Import retour factures -->
          <v-col cols="12" md="6">
            <div class="asp-block">
              <div class="asp-block-title">
                <v-icon small color="#4caf50" class="mr-1">mdi-download</v-icon>
                Importer le retour ASP
              </div>
              <v-file-input
                v-model="factImportFile"
                label="Fichier JSON retour ASP"
                accept="application/json"
                outlined
                dense
                prepend-icon="mdi-file-code-outline"
                class="mt-2"
              ></v-file-input>
              <v-btn
                color="#ff9800"
                dark
                :loading="loadingImportFact"
                :disabled="!factImportFile"
                @click="importFactures"
              >
                Importer le retour
                <v-icon right>mdi-file-import</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-progress-linear
        v-if="loadingExportFact || loadingImportFact"
        indeterminate
        color="#ff9800"
      ></v-progress-linear>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>

    <!-- Dialog de confirmation -->
    <v-dialog v-model="confirmDialog" max-width="450">
      <v-card>
        <v-card-title class="text-h6">Confirmation</v-card-title>
        <v-card-text>{{ confirmMessage }}</v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">Annuler</v-btn>
          <v-btn color="#e2007a" dark @click="onConfirm">Continuer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import toolsFormat from "@/tools/toolsFormat.js";
import server from "../store/server.js";
import axios from "axios";
import JSZip from "jszip";
import saveAs from "file-saver";

export default {
  name: "SegurASP",

  data() {
    return {
      stats: {},

      nbExport: "200",
      loadingExport: false,
      exportErrors: null,
      lastExportDate: null,

      bdcImportFile: null,
      loadingImport: false,
      importResult: null,
      lastImportDate: null,

      nbExportFact: "200",
      loadingExportFact: false,

      factImportFile: null,
      loadingImportFact: false,

      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",

      confirmDialog: false,
      confirmMessage: "",
      pendingAction: null,

      exportOptions: [
        { text: "1", value: "1" },
        { text: "2", value: "2" },
        { text: "10", value: "10" },
        { text: "50", value: "50" },
        { text: "100", value: "100" },
        { text: "200 (max ASP)", value: "200" },
      ],
      errorCategories: ["FORMAT", "COHERENCE", "STRUCTURE", "METIER", "INCONNU"],
    };
  },

  computed: {
    bdcAExporter() {
      const total = this.stats.bdcTotal || 0;
      const soumis = this.stats.bdcSoumisAsp || 0;
      return Math.max(0, total - soumis);
    },
  },

  async created() {
    this.loadLocalStorage();
    await this.loadStats();
  },

  methods: {

    // Formate un numero editeur a 5 chiffres avec zero-padding (ex: 1 -> "00001")
    formatNumeroEditeur(num) {
      if (num === null || num === undefined) return "-";
      return String(num).padStart(5, "0");
    },

    // Chargement des stats V2
    async loadStats() {
      try {
        const res = await axios({
          method: "GET",
          url: server.url + server.urls.segur + "/v2/stats",
          headers: server.getHeader(),
        });
        if (res && res.data && res.data.data) {
          this.stats = res.data.data;
        }
      } catch (e) {
        // Stats non critiques
      }
    },

    loadLocalStorage() {
      try {
        const exp = localStorage.getItem("oldExport");
        if (exp) this.lastExportDate = JSON.parse(exp);
      } catch (e) {
        localStorage.removeItem("oldExport");
      }
      try {
        const imp = localStorage.getItem("oldImport");
        if (imp) this.lastImportDate = JSON.parse(imp);
      } catch (e) {
        localStorage.removeItem("oldImport");
      }
    },

    // Export BDC V2
    async exportBdc() {
      if (this.lastExportDate) {
        const pendingReturn = !this.lastImportDate || this.lastExportDate > this.lastImportDate;
        if (pendingReturn) {
          this.showConfirm(
            "Un export a déjà eu lieu sans import de retour. Êtes-vous sûr de vouloir exporter à nouveau ?",
            () => this.doExportBdc()
          );
          return;
        }
      }
      await this.doExportBdc();
    },

    async doExportBdc() {
      this.loadingExport = true;
      this.exportErrors = null;
      try {
        const res = await axios({
          method: "POST",
          url: server.url + server.urls.segur + "/v2/export",
          headers: server.getHeader(),
          data: { nb: this.nbExport },
        });

        if (res && res.data && res.data.success === false) {
          if (res.data.data && res.data.data.errorSummary) {
            this.exportErrors = res.data.data;
          } else {
            const msg = (res.data.data && (res.data.data.error || res.data.data.message)) || "Erreur export";
            this.showSnackbar(msg, "error");
          }
          return;
        }

        const data = res && res.data && res.data.data;
        if (!data || !data.files || data.files.length === 0) {
          this.showSnackbar("Aucun bon de commande à exporter.", "info");
          return;
        }

        // Téléchargement ZIP (PDFs joints)
        if (data.zip_folder && data.zip_folder.length) {
          const zipName = (data.nameFile || "export-asp-v2").replace(".json", ".zip");
          await this.downloadZip(data.zip_folder, zipName);
        }

        // Téléchargement JSON(s) (un par régime TVA)
        for (const file of data.files) {
          const content = file.jsonString || JSON.stringify(file.json);
          this.downloadFile(content, file.filename);
        }

        localStorage.setItem("oldExport", Date.now());
        this.loadLocalStorage();
        this.showSnackbar(data.files.length + " fichier(s) JSON exporté(s)", "success");
        await this.loadStats();
      } catch (e) {
        this.showSnackbar("Échec de l'export.", "error");
      } finally {
        this.loadingExport = false;
      }
    },

    // Import retour BDC V2
    async importReturnBdc() {
      if (!this.bdcImportFile) return;
      this.loadingImport = true;
      this.importResult = null;
      try {
        const fileData = await this.readFileAsDataURL(this.bdcImportFile);
        const res = await axios({
          method: "POST",
          url: server.url + server.urls.segur + "/v2/import-return",
          headers: server.getHeader(),
          data: { json: fileData },
        });

        if (res && res.data && res.data.success === false) {
          const msg = (res.data.data && (res.data.data.error || res.data.data.message)) || "Format invalide";
          this.showSnackbar(msg, "error");
          return;
        }

        if (res && res.data && res.data.data) {
          this.importResult = res.data.data;
          localStorage.setItem("oldImport", Date.now());
          this.loadLocalStorage();

          const s = this.importResult.summary;
          this.showSnackbar(
            "Import terminé : " + s.accepted + " accepté(s), " + s.rejected + " rejeté(s)",
            s.rejected > 0 ? "warning" : "success"
          );
          await this.loadStats();
        }
      } catch (e) {
        this.showSnackbar("Échec de l'import.", "error");
      } finally {
        this.loadingImport = false;
      }
    },

    // Export factures (solde)
    async exportFactures() {
      this.loadingExportFact = true;
      try {
        const res = await axios({
          method: "POST",
          url: server.url + server.urls.segur + "/export/facture",
          headers: server.getHeader(),
          data: { nb: this.nbExportFact },
        });

        const data = res && res.data && res.data.data;
        if (!data) return;

        // Le backend peut renvoyer un simple succès (mise à jour sans fichier)
        if (data.sucess) {
          this.showSnackbar("Mise à jour des bons de commande réussie", "success");
          return;
        }

        if (data.zip_folder && data.zip_folder.length) {
          await this.downloadZip(data.zip_folder, data.nameFile);
        }
        if (data.fileJson) {
          this.downloadFile(JSON.stringify(data.fileJson), data.nameFile);
        }

        this.showSnackbar("Factures exportées", "success");
      } catch (e) {
        this.showSnackbar("Échec de l'export des factures.", "error");
      } finally {
        this.loadingExportFact = false;
      }
    },

    // Import retour factures
    async importFactures() {
      if (!this.factImportFile) return;
      this.loadingImportFact = true;
      try {
        const fileData = await this.readFileAsDataURL(this.factImportFile);
        const res = await axios({
          method: "POST",
          url: server.url + server.urls.segur + "/import/facture",
          headers: server.getHeader(),
          data: { json: fileData },
        });

        const data = res && res.data && res.data.data;
        if (data && data.sucess) {
          this.showSnackbar("Mise à jour des factures réussie", "success");
        }

        // Le backend peut aussi renvoyer des fichiers à télécharger
        if (data && data.zip_folder && data.zip_folder.length) {
          await this.downloadZip(data.zip_folder, data.nameFile);
        }
        if (data && data.fileJson) {
          this.downloadFile(JSON.stringify(data.fileJson), data.nameFile);
        }
      } catch (e) {
        this.showSnackbar("Échec de l'import des factures.", "error");
      } finally {
        this.loadingImportFact = false;
      }
    },

    // Utilitaires : download / lecture fichier

    downloadFile(content, filename) {
      const blob = new Blob([content], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    },

    async downloadZip(files, filename) {
      const zip = new JSZip();
      for (const f of files) {
        zip.file(f.filename, f.filedata, { base64: true });
      }
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, filename);
    },

    readFileAsDataURL(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
    },

    // Utilitaires : affichage

    formatDate(timestamp) {
      return toolsFormat.getDateLong(timestamp);
    },

    formatMontant(value) {
      if (!value && value !== 0) return "0 €";
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
      }).format(value);
    },

    statusColor(status) {
      if (status === "ACCEPTED") return "#4caf50";
      if (status === "REJECTED") return "#f44336";
      return "#ff9800";
    },

    statusIcon(status) {
      if (status === "ACCEPTED") return "mdi-check-circle";
      if (status === "REJECTED") return "mdi-close-circle";
      return "mdi-help-circle";
    },

    // Utilitaires : feedback UI

    showSnackbar(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color || "success";
      this.snackbar = true;
    },

    showConfirm(message, action) {
      this.confirmMessage = message;
      this.pendingAction = action;
      this.confirmDialog = true;
    },

    onConfirm() {
      this.confirmDialog = false;
      if (this.pendingAction) {
        this.pendingAction();
        this.pendingAction = null;
      }
    },
  },
};
</script>

<style scoped>
.kpi-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.kpi-value {
  font-size: 28px;
  font-weight: bold;
  color: #333;
}

.kpi-label {
  font-size: 12px;
  color: #888;
}

.section-title {
  font-size: 14px;
  font-weight: bold;
}

.asp-block {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px;
  height: 100%;
}

.asp-block-title {
  font-weight: bold;
  font-size: 13px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.asp-last-action {
  font-size: 12px;
  color: #888;
}

.asp-error-line {
  font-size: 13px;
  color: #f44336;
  margin-bottom: 4px;
  margin-left: 8px;
}

.asp-corrective {
  font-size: 12px;
  color: #666;
  margin-left: 16px;
  font-style: italic;
}

.asp-import-line {
  font-size: 13px;
  margin-bottom: 4px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}
</style>
